<template>
    <v-app>
        <div @contextmenu="handleTableContextmenu">
            <!-- 상단 컨트롤 영역
                 isControls=false 면 줄 전체를 안 그린다. 행이 몇 개 안 되는 표(계정 비교표 등)에서는
                 검색·페이지크기·컬럼선택이 표 자체보다 자리를 더 먹는다.
                 기본값 true 라 기존 화면은 그대로다. -->
            <div v-if="isControls" class="cdt-controls d-flex justify-content-between mb-3">
                <div class="cdt-controls__main d-flex justify-content-start w-75">
                    <v-text-field v-model="search" append-icon="mdi-magnify" label="Search" single-line hide-details
                        class="search-input" />
                    <!-- 검색칸 옆에 화면별 버튼을 붙이는 자리(예: 商品求购 批量搜索). 안 쓰면 아무것도 안 그린다 -->
                    <slot name="search-append" />

                    <button v-if="!addingFavorite"
                            type="button"
                            class="fav-add-btn"
                            :disabled="!(search && search.trim())"
                            :title="search && search.trim() ? '현재 검색어를 즐겨찾기로 저장' : '검색 후 클릭'"
                            @click="startAddFavorite">
                        <i class="fas fa-star"></i>
                    </button>
                    <div v-else class="fav-add-input-wrap">
                        <input ref="favNameInput"
                               v-model="newFavoriteName"
                               class="fav-add-input"
                               placeholder="이름 (선택)"
                               @keydown.enter="confirmAddFavorite"
                               @keydown.esc="cancelAddFavorite"
                               @blur="onFavInputBlur" />
                    </div>

                    <button v-if="activeFilterCount"
                            type="button"
                            class="filter-clear-btn"
                            title="적용된 컬럼 필터를 모두 해제"
                            @click="clearAllColumnFilters">
                        <i class="fas fa-filter"></i>
                        <span>필터 {{ activeFilterCount }} 해제</span>
                    </button>

                    <v-select :id="`${uniqueId}_perPage`" v-if="isPerPage" v-model="currentItemsPerPage"
                        @change="handlePerPageChange" :items="perPageOptions" label="Items per page" dense outlined
                        class="table-control-select" @click="handleMethod(currentItemsPerPage)" />

                    <v-select :id="`${uniqueId}_columns`" v-if="isColumn" v-model="selectedColumns"
                        :items="headers.map(header => header.text)" item-text="text" item-value="value" label="선택 컬럼 보기"
                        class="table-control-select" dense outlined multiple chips placeholder="선택 컬럼 보기">
                        <template v-slot:selection="{ item, index }">
                            <v-chip v-if="index === 0">
                                <span>{{ item }}</span>
                            </v-chip>
                            <span v-if="index === 1" class="grey--text text-caption">
                                (+{{ selectedColumns.length - 1 }} others)
                            </span>
                        </template>
                    </v-select>

                    <v-select :id="`${uniqueId}_fixed`" v-model="selectedFixedColumn" :items="headers" item-text="text"
                        item-value="value" label="고정할 열 선택" class="table-control-select" dense outlined clearable />
                </div>

                <div v-if="isRadio" class="radio-container">
                    <label class="mr-2">높이고정</label>
                    <div class="custom-control custom-radio custom-control-inline">
                        <input :id="`radio1_${uniqueId}`" :name="`radioGroup_${uniqueId}`" type="radio"
                            class="custom-control-input" v-model="radioCheck" @change="handleRadioChange(true)"
                            :value="true">
                        <label class="custom-control-label" :for="`radio1_${uniqueId}`"> Y </label>
                    </div>
                    <div class="custom-control custom-radio custom-control-inline">
                        <input :id="`radio2_${uniqueId}`" :name="`radioGroup_${uniqueId}`" type="radio"
                            class="custom-control-input" v-model="radioCheck" @change="handleRadioChange(false)"
                            :value="false">
                        <label class="custom-control-label" :for="`radio2_${uniqueId}`"> N </label>
                    </div>
                </div>
            </div>

            <!-- 즐겨찾기 칩 행 — 검색창을 안 그리면 즐겨찾기도 쓸 수 없으므로 같이 숨긴다 -->
            <div v-if="isControls && favorites.length" class="fav-row">
                <button v-for="(fav, idx) in favorites"
                        :key="`fav-${idx}-${fav.query}`"
                        type="button"
                        class="fav-chip"
                        :class="{ 'fav-chip--active': isFavActive(fav) }"
                        :title="fav.query"
                        @click="applyFavorite(fav)">
                    <span class="fav-chip-name">{{ fav.name }}</span>
                    <span class="fav-chip-remove"
                          title="삭제"
                          @click.stop="removeFavorite(idx)">×</span>
                </button>
            </div>

            <!-- 테이블 영역 -->
            <div class="table-scroll-container" :class="{ 'no-fixed-height': !radioCheck }">
                <div class="table-wrapper">
                    <!-- 고정 컬럼 테이블 -->
                    <div v-if="selectedFixedColumn && showFixedTable" class="fixed-table-container"
                        :class="{ 'show-fixed': isScrolled }">
                        <v-data-table :headers="fixedHeaders" :items="currentPageItems" :height="tableHeight"
                            :hide-default-footer="true" :show-select="showSelect" v-model="selected"
                            :single-select="singleSelect" :page.sync="currentPage" :items-per-page="currentItemsPerPage"
                            :item-class="rowClass || undefined"
                            fixed-header class="fixed-table sticky-header elevation-0">
                            <!-- 체크박스 슬롯 -->
                            <template v-if="!hideCheckboxAll" v-slot:header.data-table-select>
                                <v-simple-checkbox :value="selected.length === sortedItems.length"
                                    :indeterminate="selected.length > 0 && selected.length < sortedItems.length"
                                    @input="selectAllItems" />
                            </template>

                            <template v-slot:item.data-table-select="{ item }">
                                <v-simple-checkbox :value="selected.includes(item)" @input="toggleSelectItem(item)" />
                            </template>

                            <!-- 고정 테이블 헤더별 도움말 아이콘 적용 -->
                            <template v-for="header in fixedHeaders" v-slot:[`header.${header.value}`]="{ header: headerData }">
                                <div :key="header.value" class="header-with-help-vertical" style="white-space: pre-line">
                                    <!-- 열 묶음 뱃지. headers[].group 이 있을 때만 나온다(없던 화면은 그대로) -->
                                    <span v-if="headerData.group" class="header-group-badge"
                                          :class="headerData.groupClass">{{ headerData.group }}</span>
                                    <span class="header-label-row">
                                        <span>{{ headerData.text }}</span>
                                        <i class="fas fa-filter col-filter-icon"
                                           :class="{ 'active': isColumnFiltered(header.value) }"
                                           title="이 열 필터"
                                           @click.stop="openFilterPanel(header, { x: $event.clientX, y: $event.clientY })"></i>
                                    </span>
                                    <i v-if="headerTooltips[header.value]"
                                       class="fas fa-question-circle help-icon-bottom mt-1"
                                       @click.stop="toggleTooltip(header.value, $event)"
                                       :class="{ 'active': activeTooltip === header.value }"></i>
                                </div>
                            </template>

                            <!-- Subheader for fixed table -->
                            <template v-if="subheader && subheader.length > 0" v-slot:body.prepend>
                                <tr class="subheader-row">
                                    <td v-if="showSelect" class="subheader-cell"></td>
                                    <td v-for="(header, index) in fixedHeaders" :key="index" class="subheader-cell">
                                        <slot v-if="subheader[getOriginalHeaderIndex(header.value)]?.isSlot"
                                              :name="`subheader-${header.value}`" />
                                        <span v-else>{{ subheader[getOriginalHeaderIndex(header.value)]?.text || '' }}</span>
                                    </td>
                                </tr>
                            </template>

                            <!-- 고정 열 데이터 슬롯 -->
                            <template v-for="(header, index) in fixedHeaders"
                                v-slot:[`item.${header.value}`]="{ item }">
                                <div :key="index">
                                    <slot v-if="header.isSlot" :name="`${header.value}`" :item="item" />
                                    <span v-else>{{ item[header.value] }}</span>
                                </div>
                            </template>
                        </v-data-table>
                    </div>

                    <!-- 메인 테이블 -->
                    <v-data-table ref="mainTable" :id="'data_table_'.concat(uniqueId)" v-model="selected"
                        :headers="selectedItems" :height="tableHeight" :show-select="showSelect" :items="sortedItems"
                        :sort-by.sync="sortBy" :sort-desc.sync="sortDesc" :custom-sort="identitySort"
                        :page.sync="currentPage"
                        :items-per-page="currentItemsPerPage" @input="onInput" :item-key="itemKey"
                        @page-count="pageCount = $event" fixed-header hide-default-footer
                        :item-class="rowClass || undefined"
                        class="main-table sticky-header elevation-0" @scroll="handleScroll">
                        <template v-if="hideCheckboxAll" v-slot:header.data-table-select></template>

                        <!-- 각 헤더별 도움말 아이콘 적용 -->
                        <template v-for="header in headers" v-slot:[`header.${header.value}`]="{ header: headerData }">
                            <div :key="header.value" class="header-with-help-vertical" style="white-space: pre-line">
                                <!-- 열 묶음 뱃지. headers[].group 이 있을 때만 나온다(없던 화면은 그대로) -->
                                <span v-if="headerData.group" class="header-group-badge"
                                      :class="headerData.groupClass">{{ headerData.group }}</span>
                                <span class="header-label-row">
                                    <span>{{ headerData.text }}</span>
                                    <i class="fas fa-filter col-filter-icon"
                                       :class="{ 'active': isColumnFiltered(header.value) }"
                                       title="이 열 필터"
                                       @click.stop="openFilterPanel(header, { x: $event.clientX, y: $event.clientY })"></i>
                                </span>
                                <i v-if="headerTooltips[header.value]"
                                   class="fas fa-question-circle help-icon-bottom mt-1"
                                   @click.stop="toggleTooltip(header.value, $event)"
                                   :class="{ 'active': activeTooltip === header.value }"></i>
                            </div>
                        </template>

                        <!-- Subheader for main table -->
                        <template v-if="subheader && subheader.length > 0" v-slot:body.prepend>
                            <tr class="subheader-row">
                                <td v-if="showSelect" class="subheader-cell"></td>
                                <td v-for="(header, index) in selectedItems" :key="index" class="subheader-cell">
                                    <slot v-if="subheader[getOriginalHeaderIndex(header.value)]?.isSlot"
                                          :name="`subheader-${header.value}`" />
                                    <span v-else>{{ subheader[getOriginalHeaderIndex(header.value)]?.text || '' }}</span>
                                </td>
                            </tr>
                        </template>

                        <template v-for="(header, index) in headers" v-slot:[`item.${header.value}`]="{ item }">
                            <div :key="index">
                                <slot v-if="header.isSlot" :name="`${header.value}`" :item="item" />
                                <span v-else>{{ item[header.value] }}</span>
                            </div>
                        </template>
                    </v-data-table>
                </div>
            </div>

            <!-- Tooltip 표시 영역 -->
            <div v-if="activeTooltip && headerTooltips[activeTooltip]"
                 class="tooltip-display"
                 :style="tooltipStyle">
                <div class="tooltip-content">
                    <div class="tooltip-header">
                        <strong>{{ getHeaderText(activeTooltip) }}</strong>
                        <i class="fas fa-times tooltip-close" @click="closeTooltip"></i>
                    </div>
                    <div class="tooltip-body">
                        {{ headerTooltips[activeTooltip] }}
                    </div>
                </div>
            </div>

            <!-- 헤더 우클릭 컨텍스트 메뉴 -->
            <div v-if="colMenu.open"
                 class="col-context-menu"
                 :style="{ top: colMenu.top + 'px', left: colMenu.left + 'px' }"
                 @click.stop
                 @contextmenu.stop.prevent>
                <div class="col-menu-item"
                     @click="openFilterPanel(colMenu.header, { x: colMenu.left, y: colMenu.top })">
                    이 열 필터
                </div>
                <div v-if="colMenu.header && isColumnFiltered(colMenu.header.value)"
                     class="col-menu-item"
                     @click="clearColumnFilter(colMenu.header.value)">
                    이 열 필터 해제
                </div>
                <div class="col-menu-item"
                     :class="{ disabled: !canHideMore }"
                     @click="canHideMore && hideColumn(colMenu.header)">
                    이 열 숨기기
                </div>
                <div v-if="hiddenColumns.length"
                     class="col-menu-item has-submenu"
                     @mouseenter="colMenu.showHidden = true"
                     @mouseleave="colMenu.showHidden = false">
                    <span>숨긴 열 보이기 ({{ hiddenColumns.length }})</span>
                    <span class="submenu-arrow">▶</span>
                    <div v-if="colMenu.showHidden" class="col-submenu">
                        <div v-for="v in hiddenColumns" :key="v"
                             class="col-menu-item"
                             @click="showColumn(v)">
                            {{ headerTextByValue(v) }}
                        </div>
                    </div>
                </div>
                <div v-if="hiddenColumns.length"
                     class="col-menu-item"
                     @click="showAllColumns">
                    모든 열 보이기
                </div>
            </div>

            <!-- 컬럼 필터 패널 -->
            <div v-if="filterPanel.open"
                 class="col-filter-panel"
                 :style="{ top: filterPanel.top + 'px', left: filterPanel.left + 'px' }"
                 @click.stop
                 @contextmenu.stop.prevent>
                <div class="cf-title">{{ filterPanel.header ? filterPanel.header.text : '' }}</div>

                <div class="cf-sort">
                    <button type="button" class="cf-sort-btn" @click="applySortFromPanel(false)">
                        <i class="fas fa-arrow-up"></i> 오름차순
                    </button>
                    <button type="button" class="cf-sort-btn" @click="applySortFromPanel(true)">
                        <i class="fas fa-arrow-down"></i> 내림차순
                    </button>
                </div>

                <div class="cf-tabs">
                    <div class="cf-tab" :class="{ active: filterPanel.mode === 'values' }"
                         @click="filterPanel.mode = 'values'">값 목록</div>
                    <div class="cf-tab" :class="{ active: filterPanel.mode === 'condition' }"
                         @click="filterPanel.mode = 'condition'">조건</div>
                </div>

                <div v-if="filterPanel.mode === 'values'" class="cf-values">
                    <input v-model="filterPanel.keyword" class="cf-search" placeholder="값 검색" />
                    <label class="cf-option cf-option-all">
                        <input type="checkbox" :checked="isAllFilterValuesChecked" @change="toggleAllFilterValues" />
                        <span class="cf-option-label">전체</span>
                        <span class="cf-option-count">{{ visibleFilterValueOptions.length }}</span>
                    </label>
                    <div class="cf-option-list">
                        <label v-for="opt in visibleFilterValueOptions" :key="opt.value" class="cf-option">
                            <input type="checkbox"
                                   :checked="filterPanel.draftSelected.includes(opt.value)"
                                   @change="toggleFilterValue(opt.value)" />
                            <span class="cf-option-label" :title="opt.label">{{ opt.label }}</span>
                            <span class="cf-option-count">{{ opt.count }}</span>
                        </label>
                        <div v-if="!visibleFilterValueOptions.length" class="cf-empty">표시할 값이 없습니다</div>
                    </div>
                    <div v-if="hiddenFilterOptionCount" class="cf-hint">
                        외 {{ hiddenFilterOptionCount }}개 — 검색으로 좁혀주세요
                    </div>
                </div>

                <div v-else class="cf-condition">
                    <div v-for="(row, i) in filterPanel.draftOps" :key="i" class="cf-cond-row">
                        <select v-model="row.op" class="cf-cond-op">
                            <option value="">조건</option>
                            <option v-for="op in filterOpOptions" :key="op.value" :value="op.value">{{ op.text }}</option>
                        </select>
                        <input v-model="row.val" class="cf-cond-val" placeholder="값"
                               @keydown.enter="applyColumnFilter" />
                    </div>
                    <div class="cf-hint">두 조건은 AND로 묶입니다</div>
                </div>

                <div class="cf-actions">
                    <button type="button" class="cf-btn" @click="clearColumnFilter()">해제</button>
                    <button type="button" class="cf-btn cf-btn-apply" @click="applyColumnFilter">적용</button>
                </div>
            </div>

            <!-- 페이지네이션 -->
            <v-pagination v-if="sortedItems.length > currentItemsPerPage" v-model="currentPage"
                :length="Math.ceil(sortedItems.length / currentItemsPerPage)" :total-visible="15" @input="changePage"
                class="mt-3" />
        </div>
    </v-app>
</template>

<script>
import { v4 } from 'uuid'

export default {
    name: 'CDataTable',
    data() {
        return {
            uniqueId: v4(),
            selected: [],
            search: '',
            currentPage: 1,
            currentItemsPerPage: this.itemsPerPage || 10,
            perPageOptions: [10, 25, 50, 100],
            radioCheck: true,
            hiddenColumns: [],
            favorites: [],
            addingFavorite: false,
            newFavoriteName: '',
            colMenu: {
                open: false,
                header: null,
                top: 0,
                left: 0,
                showHidden: false
            },
            columns: [],
            sortBy: [],
            sortDesc: [],
            // 정렬 우선순위 목록 [{ key, desc }]. 0번이 주 정렬, 뒤쪽은 동점 시 tie-break (엑셀 방식)
            sortKeys: [],
            sortSyncing: false,
            sortSyncScheduled: false,
            // 컬럼별 필터. { [header.value]: { mode: 'values'|'condition', selected: [], ops: [] } } — 컬럼 간 AND
            columnFilters: {},
            filterPanel: {
                open: false,
                header: null,
                top: 0,
                left: 0,
                mode: 'values',
                keyword: '',
                draftSelected: [],
                draftOps: []
            },
            maxFilterOptions: 300,
            isScrolled: false,
            selectedFixedColumn: this.fixedColumns,
            showFixedTable: true,
            pageCount: 0,
            tableHeight: this.height || '600px',
            textToValueMap: {},
            // Add unique identifiers for form controls
            radioId1: `radio1_${v4()}`,
            radioId2: `radio2_${v4()}`,
            selectId: `select_${v4()}`,
            // Tooltip 관련
            activeTooltip: null,
            tooltipStyle: {}
        }
    },
    props: {
        headers: {
            type: Array,
            required: true
        },
        height: {
            type: String,
            default: '600px'
        },
        showSelect: Boolean,
        countInit: Number,
        singleSelect: Boolean,
        hideCheckboxAll: Boolean,
        totalCount: Number,
        items: {
            type: Array,
            required: true
        },
        itemKey: {
            type: String,
            required: true
        },
        page: Number,
        isPerPage: {
            type: Boolean,
            default: true
        },
        isColumn: {
            type: Boolean,
            default: true
        },
        isRadio: {
            type: Boolean,
            default: true
        },
        // 상단 컨트롤 줄(검색·즐겨찾기·페이지크기·컬럼선택·고정열선택·높이고정)을 통째로 끈다.
        // 개별 isPerPage/isColumn/isRadio 로는 검색창과 '고정할 열 선택' 이 안 꺼져서 줄이 남는다.
        // 고정 열은 fixedColumns prop 으로 이미 정해지므로(selectedFixedColumn 초기값) 숨겨도 유지된다.
        isControls: {
            type: Boolean,
            default: true
        },
        // 정렬 때 값 없음(null·undefined)을 방향과 무관하게 끝에 둔다. 끄면(기본) 예전 비교 그대로 —
        // null 이 0 자리에 섞여 «모름» 이 맨 앞에 서던 것을 켠 표만 고친다(keywordCollect · couponprice0-3).
        nullsLast: { type: Boolean, default: false },
        initialSelectedColumns: {
            type: Array,
            default: () => []
        },
        fixedColumns: {
            type: String,
            default: null
        },
        itemsPerPage: {
            type: Number,
            default: 10
        },
        subheader: {
            type: Array,
            default: () => []
        },
        headerTooltips: {
            type: Object,
            default: () => ({})
        },
        rowClass: {
            type: Function,
            default: null
        },
        storageKey: {
            type: String,
            default: null
        },
        // 검색창이 최상위 값만 보므로, 안쪽 객체에 있는 값(예: 주문정보['1688주문번호'])을
        // 검색에 태우고 싶을 때 item → 문자열을 돌려주는 함수를 넘긴다. 없으면 기존 동작 그대로
        searchText: {
            type: Function,
            default: null
        }
    },
    methods: {
        // 이미 sortedItems에서 정렬을 끝냈으므로 Vuetify 내부 정렬은 건너뛴다
        identitySort(items) {
            return items
        },
        // sortBy/sortDesc 변경이 여러 번 emit 되므로 nextTick에 한 번만 정리
        scheduleSortSync() {
            if (this.sortSyncing || this.sortSyncScheduled) return
            this.sortSyncScheduled = true
            this.$nextTick(() => {
                this.sortSyncScheduled = false
                this.reconcileSortKeys()
            })
        },
        // Vuetify가 갱신한 sortBy/sortDesc를 우선순위 목록으로 변환.
        // 방금 클릭한 컬럼을 맨 앞으로 올리고 이전 정렬은 뒤에 남겨 tie-break로 쓴다.
        reconcileSortKeys() {
            const MAX_SORT_KEYS = 3
            const by = this.sortBy || []
            const desc = this.sortDesc || []
            const prev = this.sortKeys
            const incoming = by.map((key, i) => ({ key, desc: !!desc[i] }))
            let next

            if (!incoming.length) {
                next = []
            } else {
                const added = incoming.filter(k => !prev.some(p => p.key === k.key))
                if (added.length) {
                    // 새 컬럼 클릭 → Vuetify가 배열을 비우고 새 키만 넣은 상태. 이전 우선순위를 뒤에 복원
                    const promoted = added[added.length - 1]
                    next = [promoted, ...prev.filter(p => p.key !== promoted.key)]
                } else {
                    // 기존 컬럼 재클릭(방향 토글) 또는 정렬 해제
                    const kept = prev
                        .filter(p => incoming.some(k => k.key === p.key))
                        .map(p => ({ key: p.key, desc: incoming.find(k => k.key === p.key).desc }))
                    const flipped = kept.find(k => prev.some(p => p.key === k.key && p.desc !== k.desc))
                    next = flipped ? [flipped, ...kept.filter(k => k.key !== flipped.key)] : kept
                }
            }

            next = next.slice(0, MAX_SORT_KEYS)
            this.sortKeys = next

            // 헤더 화살표 표시와 다음 클릭 동작을 위해 Vuetify에도 전체 키를 되돌려준다
            const nextBy = next.map(k => k.key)
            const nextDesc = next.map(k => k.desc)
            const changed = nextBy.join(',') !== by.join(',') ||
                nextDesc.join(',') !== desc.slice(0, nextDesc.length).join(',')
            if (changed) {
                this.sortSyncing = true
                this.sortBy = nextBy
                this.sortDesc = nextDesc
                this.$nextTick(() => {
                    this.sortSyncing = false
                })
            }
        },
        // ===== 컬럼 필터 =====
        filterKeyOf(raw) {
            return raw === null || raw === undefined ? '' : String(raw)
        },
        // "1,234원" 같은 표기도 숫자로 읽는다. 숫자가 아니면 NaN
        toFilterNumber(raw) {
            if (raw === null || raw === undefined) return NaN
            const s = String(raw).replace(/[^0-9.-]/g, '')
            if (!s || s === '-' || s === '.') return NaN
            return Number(s)
        },
        // 값 샘플이 대부분 숫자면 숫자 컬럼으로 보고 조건 탭을 기본으로 연다
        detectNumericColumn(key, items) {
            let checked = 0
            let numeric = 0
            for (const item of items) {
                const raw = item[key]
                if (raw === null || raw === undefined || raw === '') continue
                checked++
                if (Number.isFinite(this.toFilterNumber(raw))) numeric++
                if (checked >= 50) break
            }
            return checked > 0 && numeric / checked >= 0.8
        },
        matchesColumnFilters(item, exceptKey) {
            for (const key of Object.keys(this.columnFilters)) {
                if (key === exceptKey) continue
                if (!this.matchesFilter(item[key], this.columnFilters[key])) return false
            }
            return true
        },
        matchesFilter(raw, filter) {
            if (!filter) return true
            if (filter.mode === 'values') return filter.selected.includes(this.filterKeyOf(raw))
            return filter.ops.every(op => this.matchesCondition(raw, op))
        },
        matchesCondition(raw, op) {
            const target = String(op.val === null || op.val === undefined ? '' : op.val).trim()
            if (!target) return true
            const text = this.filterKeyOf(raw).toLowerCase()
            const needle = target.toLowerCase()
            const a = this.toFilterNumber(raw)
            const b = this.toFilterNumber(target)
            const bothNumeric = Number.isFinite(a) && Number.isFinite(b)

            switch (op.op) {
                case 'gte': return bothNumeric ? a >= b : false
                case 'lte': return bothNumeric ? a <= b : false
                case 'eq': return bothNumeric ? a === b : text === needle
                case 'ne': return bothNumeric ? a !== b : text !== needle
                case 'contains': return text.includes(needle)
                case 'notContains': return !text.includes(needle)
                case 'startsWith': return text.startsWith(needle)
                default: return true
            }
        },
        isColumnFiltered(headerValue) {
            return Object.prototype.hasOwnProperty.call(this.columnFilters, headerValue)
        },
        openFilterPanel(header, pos) {
            if (!header || header.value === 'data-table-select') return
            this.closeColumnMenu()

            const existing = this.columnFilters[header.value]
            const numeric = this.detectNumericColumn(header.value, this.filteredItems)
            const ops = existing && existing.mode === 'condition' ? existing.ops : []

            this.filterPanel = {
                open: true,
                header,
                top: Math.max(8, Math.min(pos.y + 4, window.innerHeight - 400)),
                left: Math.max(8, Math.min(pos.x, window.innerWidth - 280)),
                mode: existing ? existing.mode : (numeric ? 'condition' : 'values'),
                keyword: '',
                draftSelected: existing && existing.mode === 'values' ? [...existing.selected] : [],
                draftOps: [
                    { op: ops[0] ? ops[0].op : '', val: ops[0] ? ops[0].val : '' },
                    { op: ops[1] ? ops[1].op : '', val: ops[1] ? ops[1].val : '' }
                ]
            }

            // 필터가 없던 컬럼은 엑셀처럼 전체 체크 상태로 시작
            if (!existing || existing.mode !== 'values') {
                this.filterPanel.draftSelected = this.filterValueOptions.map(o => o.value)
            }

            this.$nextTick(() => {
                document.addEventListener('click', this.handleFilterPanelOutsideClick)
                document.addEventListener('keydown', this.handleFilterPanelKeydown)
            })
        },
        closeFilterPanel() {
            if (!this.filterPanel.open) return
            this.filterPanel.open = false
            this.filterPanel.header = null
            document.removeEventListener('click', this.handleFilterPanelOutsideClick)
            document.removeEventListener('keydown', this.handleFilterPanelKeydown)
        },
        handleFilterPanelOutsideClick(e) {
            if (!e.target.closest('.col-filter-panel')) this.closeFilterPanel()
        },
        handleFilterPanelKeydown(e) {
            if (e.key === 'Escape') this.closeFilterPanel()
        },
        toggleFilterValue(value) {
            const selected = this.filterPanel.draftSelected
            const idx = selected.indexOf(value)
            if (idx >= 0) selected.splice(idx, 1)
            else selected.push(value)
        },
        toggleAllFilterValues() {
            const visible = this.visibleFilterValueOptions.map(o => o.value)
            if (this.isAllFilterValuesChecked) {
                this.filterPanel.draftSelected = this.filterPanel.draftSelected.filter(v => !visible.includes(v))
            } else {
                const merged = new Set([...this.filterPanel.draftSelected, ...visible])
                this.filterPanel.draftSelected = Array.from(merged)
            }
        },
        applyColumnFilter() {
            if (!this.filterPanel.header) return
            const key = this.filterPanel.header.value
            const next = { ...this.columnFilters }

            if (this.filterPanel.mode === 'values') {
                const all = this.filterValueOptions.map(o => o.value)
                const selected = this.filterPanel.draftSelected.filter(v => all.includes(v))
                // 전부 선택 = 필터 없음
                if (selected.length === all.length) delete next[key]
                else next[key] = { mode: 'values', selected }
            } else {
                const ops = this.filterPanel.draftOps
                    .filter(o => o.op && String(o.val).trim() !== '')
                    .map(o => ({ op: o.op, val: o.val }))
                if (!ops.length) delete next[key]
                else next[key] = { mode: 'condition', ops }
            }

            this.columnFilters = next
            this.currentPage = 1
            this.closeFilterPanel()
        },
        clearColumnFilter(headerValue) {
            const key = headerValue || (this.filterPanel.header && this.filterPanel.header.value)
            if (!key) return
            const next = { ...this.columnFilters }
            delete next[key]
            this.columnFilters = next
            this.currentPage = 1
            this.closeFilterPanel()
        },
        clearAllColumnFilters() {
            this.columnFilters = {}
            this.currentPage = 1
            this.closeFilterPanel()
        },
        // 필터 패널에서 바로 정렬. 해당 컬럼을 1순위로 올린다
        applySortFromPanel(desc) {
            if (!this.filterPanel.header) return
            const key = this.filterPanel.header.value
            const next = [{ key, desc }, ...this.sortKeys.filter(k => k.key !== key)].slice(0, 3)
            this.sortKeys = next
            this.sortSyncing = true
            this.sortBy = next.map(k => k.key)
            this.sortDesc = next.map(k => k.desc)
            this.$nextTick(() => {
                this.sortSyncing = false
            })
            this.closeFilterPanel()
        },
        // Add namespace to radio handlers
        handleRadioChange(value) {
            this.radioCheck = value;
            this.updateTableHeight();
        },
        // Update select handlers
        handlePerPageChange(value) {
            this.currentItemsPerPage = value;
            this.persistState();
            this.$emit('click', value);
        },

        handleColumnSelect(value) {
            this.selectedColumns = value;
            this.$nextTick(() => {
                this.syncHeaderHeights();
            });
        },
        columnItems() {
            return this.headers.map(header => ({
                text: header.text,
                value: header.value
            }))
        },
        onInput(eventvalue) {
            this.$emit('input', eventvalue)
        },
        handleMethod(eventvalue) {
            this.$emit('click', eventvalue)
        },
        itemsLength(eventValue) {
            this.$emit('itemsLength', eventValue)
        },
        changePage(value) {
            this.currentPage = value
            this.isScrolled = false
            this.showFixedTable = false

            this.$nextTick(() => {
                setTimeout(() => {
                    this.showFixedTable = true
                    this.syncHeaderHeights()
                }, 50)

                this.emitChangePage()
            })
        },
        emitChangePage() {
            this.$emit('changePage', this.currentPage)
        },
        handleScroll(e) {
            const target = e.target || e.srcElement
            const wasScrolled = this.isScrolled
            this.isScrolled = target.scrollLeft > 0
            // 고정열이 막 보일 때 행 높이를 다시 잰다(그린 뒤 그림·열 너비로 본문 행이 바뀌었을 수 있음)
            if (this.isScrolled && !wasScrolled) this.syncHeaderHeights()

            const fixedTableContainer = this.$el.querySelector('.fixed-table-container')
            if (fixedTableContainer) {
                const fixedWrapper = fixedTableContainer.querySelector('.v-data-table__wrapper')
                if (fixedWrapper) {
                    fixedWrapper.scrollTop = target.scrollTop
                }
            }
        },
        selectAllItems(value) {
            if (value) {
                this.selected = [...this.sortedItems]
            } else {
                this.selected = []
            }
            this.$emit('input', this.selected)
        },
        toggleSelectItem(item) {
            const index = this.selected.findIndex(i => i[this.itemKey] === item[this.itemKey])
            if (index === -1) {
                this.selected.push(item)
            } else {
                this.selected.splice(index, 1)
            }
            this.$emit('input', this.selected)
        },
        syncHeaderHeights() {
            this.$nextTick(() => {
                if (!this.$refs.mainTable || !this.$refs.mainTable.$el) return;

                const mainHeaders = this.$refs.mainTable.$el.querySelectorAll('th')
                const fixedTableContainer = this.$el.querySelector('.fixed-table-container')

                if (fixedTableContainer) {
                    const fixedHeaders = fixedTableContainer.querySelectorAll('th')
                    mainHeaders.forEach((header, index) => {
                        if (fixedHeaders[index]) {
                            const height = getComputedStyle(header).height
                            // 머리칸 CSS 가 `height: unset !important` 라 그냥 넣으면 안 먹힌다 → important 로(#44)
                            fixedHeaders[index].style.setProperty('height', height, 'important')
                        }
                    })
                    // 고정열 표는 그 열 내용만큼만 높다 → 본문 행 높이를 옮겨야 가로로 밀 때 행이 계단식으로 안 어긋난다(딩톡 #37)
                    const mainRows = this.$refs.mainTable.$el.querySelectorAll('tbody tr')
                    const fixedRows = fixedTableContainer.querySelectorAll('tbody tr')
                    mainRows.forEach((row, index) => {
                        if (fixedRows[index]) {
                            fixedRows[index].style.height = `${row.getBoundingClientRect().height}px`
                        }
                    })
                }
            })
        },
        updateTableHeight() {
            if (!this.radioCheck) {
                this.tableHeight = 'auto'
            } else {
                this.tableHeight = this.height || '600px'
            }
        },
        getOriginalHeaderIndex(headerValue) {
            return this.headers.findIndex(header => header.value === headerValue)
        },
        // Tooltip 관련 메서드들
        toggleTooltip(headerValue, event) {
            if (this.activeTooltip === headerValue) {
                this.closeTooltip()
            } else {
                this.showTooltip(headerValue, event)
            }
        },
        showTooltip(headerValue, event) {
            this.activeTooltip = headerValue

            // 클릭된 아이콘의 위치를 기준으로 tooltip 위치 계산
            const rect = event.target.getBoundingClientRect()
            const containerRect = this.$el.getBoundingClientRect()

            this.tooltipStyle = {
                position: 'absolute',
                top: `${rect.bottom - containerRect.top + 10}px`,
                left: `${rect.left - containerRect.left}px`,
                zIndex: 1000
            }

            // 외부 클릭 감지를 위한 이벤트 리스너 추가
            this.$nextTick(() => {
                document.addEventListener('click', this.handleOutsideClick)
            })
        },
        closeTooltip() {
            this.activeTooltip = null
            this.tooltipStyle = {}
            document.removeEventListener('click', this.handleOutsideClick)
        },
        handleOutsideClick(event) {
            // tooltip 영역이나 도움말 아이콘을 클릭한 경우가 아니면 tooltip 닫기
            if (!event.target.closest('.tooltip-display') && !event.target.closest('.help-icon')) {
                this.closeTooltip()
            }
        },
        getHeaderText(headerValue) {
            const header = this.headers.find(h => h.value === headerValue)
            return header ? header.text.replace(/\n/g, ' ') : ''
        },
        headerTextByValue(value) {
            const h = this.headers.find(x => x.value === value)
            return h ? (h.text || '').replace(/\n/g, ' ') : value
        },
        // 헤더 영역 어디서 우클릭하든 단일 진입점 (Vuetify의 th 전체 커버)
        handleTableContextmenu(event) {
            const th = event.target.closest('th')
            if (!th) return  // 헤더가 아니면 브라우저 기본 메뉴

            const isFixed = !!th.closest('.fixed-table-container')
            const headersList = isFixed ? this.fixedHeaders : this.selectedItems

            // showSelect 시 첫 컬럼은 체크박스
            const offset = this.showSelect ? 1 : 0
            const headerIndex = th.cellIndex - offset
            if (headerIndex < 0) return  // 체크박스 컬럼 → 브라우저 기본 메뉴

            const header = headersList[headerIndex]
            if (!header) return

            event.preventDefault()
            this.openColumnMenu(header, event)
        },
        // 헤더 우클릭 컨텍스트 메뉴
        openColumnMenu(header, event) {
            if (!header || header.value === 'data-table-select') return
            this.colMenu = {
                open: true,
                header,
                top: event.clientY,
                left: event.clientX,
                showHidden: false
            }
            this.$nextTick(() => {
                document.addEventListener('click', this.closeColumnMenu)
                document.addEventListener('keydown', this.handleMenuKeydown)
                document.addEventListener('contextmenu', this.handleOutsideContextmenu, true)
            })
        },
        closeColumnMenu() {
            if (!this.colMenu.open) return
            this.colMenu.open = false
            this.colMenu.showHidden = false
            document.removeEventListener('click', this.closeColumnMenu)
            document.removeEventListener('keydown', this.handleMenuKeydown)
            document.removeEventListener('contextmenu', this.handleOutsideContextmenu, true)
        },
        handleMenuKeydown(e) {
            if (e.key === 'Escape') this.closeColumnMenu()
        },
        handleOutsideContextmenu(e) {
            // 다른 헤더에서 우클릭하면 그쪽 핸들러가 다시 열어주므로, 일단 닫기만
            if (!e.target.closest('.col-context-menu')) {
                this.closeColumnMenu()
            }
        },
        hideColumn(header) {
            if (!header) return
            if (!this.canHideMore) {
                this.closeColumnMenu()
                return
            }
            if (!this.hiddenColumns.includes(header.value)) {
                this.hiddenColumns = [...this.hiddenColumns, header.value]
            }
            // 고정 영역 안의 열을 숨기면 고정 자동 해제
            if (this.selectedFixedColumn) {
                const fixedIdx = this.headers.findIndex(h => h.value === this.selectedFixedColumn)
                const hiddenIdx = this.headers.findIndex(h => h.value === header.value)
                if (hiddenIdx >= 0 && hiddenIdx <= fixedIdx) {
                    this.selectedFixedColumn = null
                }
            }
            this.closeColumnMenu()
        },
        showColumn(value) {
            this.hiddenColumns = this.hiddenColumns.filter(v => v !== value)
            this.closeColumnMenu()
        },
        showAllColumns() {
            this.hiddenColumns = []
            this.closeColumnMenu()
        },
        // 즐겨찾기 검색
        isFavActive(fav) {
            return (this.search || '') === (fav && fav.query)
        },
        startAddFavorite() {
            const q = (this.search || '').trim()
            if (!q) return
            this.newFavoriteName = q
            this.addingFavorite = true
            this.$nextTick(() => {
                const el = this.$refs.favNameInput
                if (el) {
                    el.focus()
                    el.select()
                }
            })
        },
        confirmAddFavorite() {
            if (!this.addingFavorite) return
            const query = (this.search || '').trim()
            if (!query) {
                this.cancelAddFavorite()
                return
            }
            let name = (this.newFavoriteName || '').trim()
            if (!name) name = query
            const idx = this.favorites.findIndex(f => f.query === query)
            if (idx >= 0) {
                this.$set(this.favorites, idx, { name, query })
            } else {
                this.favorites.push({ name, query })
            }
            this.addingFavorite = false
            this.newFavoriteName = ''
        },
        cancelAddFavorite() {
            this.addingFavorite = false
            this.newFavoriteName = ''
        },
        onFavInputBlur() {
            // blur 시 약간 지연 후 자동 저장 (esc cancel과의 race 방지)
            setTimeout(() => {
                if (this.addingFavorite) this.confirmAddFavorite()
            }, 120)
        },
        applyFavorite(fav) {
            if (!fav) return
            if (this.isFavActive(fav)) {
                this.search = ''  // 같은 칩 다시 누르면 검색 해제
            } else {
                this.search = fav.query
            }
        },
        removeFavorite(idx) {
            if (idx < 0 || idx >= this.favorites.length) return
            this.favorites.splice(idx, 1)
        },
        // 영속화 (storageKey 있을 때만)
        persistState() {
            if (!this.storageKey) return
            try {
                const state = {
                    hidden: this.hiddenColumns,
                    perPage: this.currentItemsPerPage,
                    fixed: this.selectedFixedColumn,
                    fitHeight: this.radioCheck,
                    favorites: this.favorites
                }
                localStorage.setItem(`cdt:v1:${this.storageKey}`, JSON.stringify(state))
            } catch (e) {
                console.error('CDataTable persistState failed', e)
            }
        },
        loadPersistedState() {
            if (!this.storageKey) return false
            try {
                const raw = localStorage.getItem(`cdt:v1:${this.storageKey}`)
                if (!raw) return false
                const state = JSON.parse(raw)
                const validValues = new Set(this.headers.map(h => h.value))
                if (Array.isArray(state.hidden)) {
                    const filtered = state.hidden.filter(v => validValues.has(v))
                    // 모든 컬럼이 숨김이 되는 사고 방지
                    if (filtered.length >= this.headers.length) {
                        this.hiddenColumns = filtered.slice(0, this.headers.length - 1)
                    } else {
                        this.hiddenColumns = filtered
                    }
                }
                if (typeof state.perPage === 'number') {
                    this.currentItemsPerPage = state.perPage
                }
                if (state.fixed && validValues.has(state.fixed) && !this.hiddenColumns.includes(state.fixed)) {
                    this.selectedFixedColumn = state.fixed
                }
                if (typeof state.fitHeight === 'boolean') {
                    this.radioCheck = state.fitHeight
                }
                if (Array.isArray(state.favorites)) {
                    this.favorites = state.favorites
                        .filter(f => f && typeof f.query === 'string' && f.query.length > 0)
                        .map(f => ({
                            name: (f.name && typeof f.name === 'string') ? f.name : f.query,
                            query: f.query
                        }))
                }
                return true
            } catch (e) {
                console.error('CDataTable loadPersistedState failed', e)
                return false
            }
        }
    },
    computed: {
        filteredItems() {
            return this.items.filter((item) => {
                if (!this.search) return true

                const searchTerms = this.search.toLowerCase().split(' ').filter(term => term.length > 0)
                const extra = this.searchText ? String(this.searchText(item) || '').toLowerCase() : ''
                return searchTerms.every(term => {
                    return Object.values(item).some(value =>
                        value != null && String(value).toLowerCase().includes(term)
                    ) || (extra && extra.includes(term))
                })
            })
        },
        // 컬럼 필터 적용 (검색 결과 위에 AND)
        columnFilteredItems() {
            if (!Object.keys(this.columnFilters).length) return this.filteredItems
            return this.filteredItems.filter(item => this.matchesColumnFilters(item, null))
        },
        activeFilterCount() {
            return Object.keys(this.columnFilters).length
        },
        // 필터 패널을 연 컬럼의 후보 데이터. 자기 자신의 필터는 빼서 다른 값도 다시 고를 수 있게 함
        filterBaseItems() {
            if (!this.filterPanel.open || !this.filterPanel.header) return []
            const except = this.filterPanel.header.value
            return this.filteredItems.filter(item => this.matchesColumnFilters(item, except))
        },
        isNumericFilterColumn() {
            if (!this.filterPanel.open || !this.filterPanel.header) return false
            return this.detectNumericColumn(this.filterPanel.header.value, this.filterBaseItems)
        },
        filterValueOptions() {
            if (!this.filterPanel.open || !this.filterPanel.header) return []
            const key = this.filterPanel.header.value
            const counts = new Map()
            this.filterBaseItems.forEach(item => {
                const k = this.filterKeyOf(item[key])
                counts.set(k, (counts.get(k) || 0) + 1)
            })
            const numeric = this.isNumericFilterColumn
            return Array.from(counts.entries())
                .map(([value, count]) => ({ value, count, label: value === '' ? '(비어 있음)' : value }))
                .sort((a, b) => {
                    if (a.value === '') return 1
                    if (b.value === '') return -1
                    if (numeric) return this.toFilterNumber(a.value) - this.toFilterNumber(b.value)
                    return a.value.localeCompare(b.value, 'ko')
                })
        },
        matchedFilterValueOptions() {
            const keyword = (this.filterPanel.keyword || '').trim().toLowerCase()
            if (!keyword) return this.filterValueOptions
            return this.filterValueOptions.filter(o => o.label.toLowerCase().includes(keyword))
        },
        visibleFilterValueOptions() {
            return this.matchedFilterValueOptions.slice(0, this.maxFilterOptions)
        },
        hiddenFilterOptionCount() {
            return Math.max(0, this.matchedFilterValueOptions.length - this.maxFilterOptions)
        },
        isAllFilterValuesChecked() {
            const visible = this.visibleFilterValueOptions
            if (!visible.length) return false
            return visible.every(o => this.filterPanel.draftSelected.includes(o.value))
        },
        filterOpOptions() {
            return this.isNumericFilterColumn
                ? [
                    { value: 'gte', text: '이상' },
                    { value: 'lte', text: '이하' },
                    { value: 'eq', text: '같음' },
                    { value: 'ne', text: '같지 않음' }
                ]
                : [
                    { value: 'contains', text: '포함' },
                    { value: 'notContains', text: '포함하지 않음' },
                    { value: 'eq', text: '같음' },
                    { value: 'startsWith', text: '(으)로 시작' }
                ]
        },
        sortedItems() {
            let items = [...this.columnFilteredItems]
            if (this.sortKeys.length) {
                items.sort((a, b) => {
                    for (const { key, desc } of this.sortKeys) {
                        let compareA = a[key]
                        let compareB = b[key]

                        if (this.nullsLast) {
                            const ea = compareA == null
                            const eb = compareB == null
                            if (ea && eb) continue
                            if (ea) return 1
                            if (eb) return -1
                        }
                        if (typeof compareA === 'string') compareA = compareA.toLowerCase()
                        if (typeof compareB === 'string') compareB = compareB.toLowerCase()

                        if (compareA < compareB) return desc ? 1 : -1
                        if (compareA > compareB) return desc ? -1 : 1
                    }
                    return 0
                })
            }
            return items
        },
        currentPageItems() {
            const start = (this.currentPage - 1) * this.currentItemsPerPage
            const end = start + this.currentItemsPerPage
            return this.sortedItems.slice(start, end)
        },
        fixedHeaders() {
            if (!this.selectedFixedColumn) return []
            const selectedIndex = this.headers.findIndex(h => h.value === this.selectedFixedColumn)
            return this.headers.slice(0, selectedIndex + 1)
        },
        selectedItems() {
            return this.headers.filter(header => !this.hiddenColumns.includes(header.value))
        },
        // 상단 v-select 호환용 (text 배열). hiddenColumns가 source of truth.
        selectedColumns: {
            get() {
                return this.headers
                    .filter(h => !this.hiddenColumns.includes(h.value))
                    .map(h => h.text)
            },
            set(textArr) {
                const arr = Array.isArray(textArr) ? textArr : []
                const visibleValues = new Set(
                    this.headers
                        .filter(h => arr.includes(h.text))
                        .map(h => h.value)
                )
                let nextHidden = this.headers
                    .filter(h => !visibleValues.has(h.value))
                    .map(h => h.value)
                // 모두 숨김 방지 — 마지막 1개는 강제로 보임 유지
                if (nextHidden.length >= this.headers.length && this.headers.length > 0) {
                    nextHidden = nextHidden.slice(0, this.headers.length - 1)
                }
                this.hiddenColumns = nextHidden
            }
        },
        canHideMore() {
            return (this.headers.length - this.hiddenColumns.length) > 1
        }
    },
    watch: {
        search() {
            this.currentPage = 1
        },
        sortBy() {
            this.scheduleSortSync()
        },
        sortDesc() {
            this.scheduleSortSync()
        },
        selectedFixedColumn(newVal) {
            if (!newVal) {
                this.isScrolled = false
            }
            this.persistState()
            this.$nextTick(() => {
                this.syncHeaderHeights()
            })
        },
        radioCheck() {
            this.updateTableHeight()
            this.persistState()
        },
        hiddenColumns: {
            handler() {
                this.persistState()
                this.$nextTick(() => {
                    this.syncHeaderHeights()
                })
            },
            deep: true
        },
        favorites: {
            handler() {
                this.persistState()
            },
            deep: true
        },
        currentPageItems: {
            handler() {
                this.$nextTick(() => {
                    this.syncHeaderHeights()
                })
            }
        },
        itemsPerPage: {
            handler(newValue) {
                this.showFixedTable = false
                this.isScrolled = false
                this.currentPage = 1

                this.$nextTick(() => {
                    const mainTableWrapper = this.$refs.mainTable.$el.querySelector('.v-data-table__wrapper')
                    if (mainTableWrapper) {
                        mainTableWrapper.scrollLeft = 0
                    }

                    setTimeout(() => {
                        this.showFixedTable = true
                        this.syncHeaderHeights()
                    }, 50)

                    this.handleMethod(newValue)
                })
            },
            immediate: false
        }
    },
    created() {
        // Initialize columns with both text and value
        this.columns = this.headers.map(header => ({
            text: header.text,
            value: header.value
        }))

        // Create a mapping of text to value
        this.textToValueMap = this.headers.reduce((map, header) => {
            map[header.text] = header.value
            return map
        }, {})

        // 초기 hiddenColumns 결정: initialSelectedColumns prop이 있으면 그 외 컬럼을 숨김으로
        if (this.initialSelectedColumns.length > 0) {
            const visibleSet = new Set()
            this.initialSelectedColumns.forEach(token => {
                const match = this.columns.find(c => c.value === token || c.text === token)
                if (match) visibleSet.add(match.value)
            })
            this.hiddenColumns = this.headers
                .filter(h => !visibleSet.has(h.value))
                .map(h => h.value)
        } else {
            this.hiddenColumns = []
        }

        // 영속화된 상태가 있으면 prop 초기값보다 우선 적용
        this.loadPersistedState()

        if (!this.page) {
            this.currentPage = 1
        } else {
            this.currentPage = this.page
        }

        this.updateTableHeight()
    },
    mounted() {
        if (this.$refs.mainTable && this.$refs.mainTable.$el) {
            const tableWrapper = this.$refs.mainTable.$el.querySelector('.v-data-table__wrapper')
            if (tableWrapper) {
                tableWrapper.addEventListener('scroll', this.handleScroll)
            }
        }

        // Add unique IDs to form controls
        const controls = this.$el.querySelectorAll('select, input[type="radio"]')
        controls.forEach(control => {
            control.id = `${this.uniqueId}_${control.id}`
        })

        this.$nextTick(() => {
            this.syncHeaderHeights()
        })
    },
    beforeDestroy() {
        const tableWrapper = this.$refs.mainTable.$el.querySelector('.v-data-table__wrapper')
        tableWrapper.removeEventListener('scroll', this.handleScroll)
        // tooltip 이벤트 리스너 정리
        document.removeEventListener('click', this.handleOutsideClick)
        // 컨텍스트 메뉴 이벤트 리스너 정리
        document.removeEventListener('click', this.closeColumnMenu)
        document.removeEventListener('keydown', this.handleMenuKeydown)
        document.removeEventListener('contextmenu', this.handleOutsideContextmenu, true)
        // 필터 패널 이벤트 리스너 정리
        document.removeEventListener('click', this.handleFilterPanelOutsideClick)
        document.removeEventListener('keydown', this.handleFilterPanelKeydown)
    }
}
</script>

<style lang="scss" scoped>
.table-scroll-container {
    position: relative;
    height: v-bind(tableHeight);
    margin-bottom: 16px;

    &.no-fixed-height {
        height: auto;
    }
}

.table-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
}

::v-deep .v-data-table {
    height: 100%;

    .v-data-table__wrapper {
        height: 100%;
        max-height: none;
        overflow: auto;
        border-radius: 10px;
        border: 1px solid #DCDFE8;
    }

    table {
        width: 100%;
        border-spacing: 0;
    }

    thead tr:first-child th {
        position: sticky !important;
        top: 0;
        z-index: 2;
        background-color: #f8f7f7 !important;
        border-bottom: thin solid rgba(0, 0, 0, 0.12);
        white-space: normal !important;

        &::after {
            content: '';
            position: absolute;
            left: 0;
            right: 0;
            bottom: 0;
            border-bottom: thin solid rgba(0, 0, 0, 0.12);
        }
    }
}

.fixed-table-container {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    z-index: 4;
    background: white;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s;
    border-right: 2px solid rgba(0, 0, 0, 0.12);

    &.show-fixed {
        opacity: 1;
        pointer-events: auto;
    }

    ::v-deep .v-data-table__wrapper {
        overflow: hidden !important;

        thead tr:first-child th {
            z-index: 5 !important;
        }
    }
}

::v-deep .v-data-table-header th {
    background-color: #f4f5fa !important;
    white-space: pre-line !important;
    height: unset !important;
    padding: 16px 8px !important;

    span {
        display: flex;
        justify-content: center !important;
        align-items: center !important;
        white-space: pre-line !important;
        min-height: unset !important;
        line-height: 1.2;
    }
}

::v-deep .v-data-table__wrapper {
    &::-webkit-scrollbar {
        width: 8px;
        height: 8px;
    }

    &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 4px;

        &:hover {
            background: #555;
        }
    }
}

// 기본 컨트롤
.table-control-select {
    width: 130px !important;
    margin-right: 12px;
    position: relative;
    z-index: 3;
}

.search-input {
    width: 250px;
    margin-right: 12px;
    position: relative;
    z-index: 3;
}

/* 폰 폭 — 컨트롤 줄이 한 줄에 안 들어가 오른쪽(고정할 열)이 잘리고 «높이고정» 이 한 글자씩 세로로 꺾였다(10-19 390 실측).
   넓은 화면은 그대로(이 폭 밑에서만 줄바꿈) */
@media (max-width: 575.98px) {
    .cdt-controls,
    .cdt-controls__main {
        flex-wrap: wrap;
        row-gap: 8px;
    }
    .cdt-controls__main {
        width: 100% !important;
    }
    .search-input {
        flex: 1 1 100%;
        margin-right: 0;
    }
    .radio-container {
        margin-left: 0;
        white-space: nowrap;
    }
}

/* 폰 카드(Vuetify 모바일 줄) — 값이 길면 이름표가 눌려 «상품/명» 처럼 꺾였다(10-19 390 AI 로켓등록 실측 · CDataTable 쓰는 화면 모두 같은 꼴).
   이름표는 한 줄로 두고 줄어들지 않게, 긴 값은 값 칸 안에서 줄바꿈. 모바일 줄은 폰 폭에만 그려져 넓은 화면은 그대로 */
::v-deep .v-data-table__mobile-row__header {
    white-space: nowrap;
    flex-shrink: 0;
}
::v-deep .v-data-table__mobile-row__cell {
    min-width: 0;
    overflow-wrap: anywhere;
}

/* 라디오 컨테이너 수정 */
.radio-container {
    margin-left: auto;
    display: flex;
    align-items: center;
    position: relative;
    z-index: 3;
    height: auto; /* 0px에서 auto로 변경 */
    top: 0; /* 20px에서 0으로 변경 */
}

/* 커스텀 라디오 컨트롤 스타일 추가 */
.custom-control {
    position: relative;
    display: inline-flex;
    align-items: center;
    min-height: 24px;
    padding-left: 1.5rem;
}

.custom-control-inline {
    display: inline-flex;
    margin-right: 1rem;
}

/* 라디오 입력 스타일 수정 */
.custom-control-input {
    position: absolute;
    z-index: 1;
    opacity: 1;
    height: 16px;
    width: 16px;
    margin: 0;
    cursor: pointer;
}

/* 라디오 라벨 스타일 */
.custom-control-label {
    position: relative;
    margin-bottom: 0;
    vertical-align: middle;
    cursor: pointer;
}

/* 라벨 정렬 수정 */
.custom-control-label::before,
.custom-control-label::after {
    position: absolute;
    top: 0.25rem;
    left: -1.5rem;
    display: block;
    width: 16px;
    height: 16px;
    content: "";
}

::v-deep {
    .v-input__slot {
        min-height: 40px !important;
        max-height: 40px !important;
    }

    .v-text-field.v-text-field--enclosed .v-text-field__details,
    .v-select.v-select--enclosed .v-select__details {
        display: none;
    }

    .v-input--dense.v-text-field--outlined .v-input__slot,
    .v-input--dense.v-select--outlined .v-input__slot {
        min-height: 40px !important;
        max-height: 40px !important;
    }

    .v-input {
        margin-top: 0 !important;
        padding-top: 0 !important;
    }

    .v-select__selections {
        min-height: 38px;
        display: flex;
        align-items: center;
    }
}

// 모달 관련
.modal-backdrop {
    z-index: 1040;
}

.modal-dialog {
    z-index: 1050;
}

::v-deep .modal-content,
.modal {

    .table-control-select,
    .search-input {
        z-index: 2055 !important;

        .v-input__slot {
            z-index: 2055 !important;
        }
    }

    .radio-container {
        z-index: 2065 !important; // 이 값을 증가

        input[type="radio"] {
            z-index: 2065 !important; // 라디오 버튼 자체의 z-index도 추가
        }
    }

    .v-menu__content {
        z-index: 2060 !important;
    }
}

// 드롭다운 메뉴
.v-menu__content {
    z-index: 1000;

    &.menuable__content__active {
        z-index: 2060 !important;
    }
}

::v-deep .v-chip {
    margin: 0 !important;
    height: fit-content;
}

::v-deep .v-pagination__navigation,
::v-deep .v-pagination__item {
    box-shadow: none;
}

.table-container {
    max-height: 900px;
    overflow: auto;

    &-fix-none {
        max-height: none;
    }
}

::v-deep .v-application--wrap {
    min-height: 0 !important;
}

.custom-control-input {
    // top:-20px;
    max-height:50px;
}

/* Subheader styles */
.subheader-row {
    background-color: #e8f5e8 !important;
    position: sticky;
    top: 48px; /* Header height */
    z-index: 1;
}

.subheader-cell {
    background-color: #e8f5e8 !important;
    font-weight: 500;
    border-bottom: 2px solid #4caf50 !important;
    padding: 8px 16px !important;
    text-align: center;
}

/* Header Help Icon Styles */
.header-with-help-vertical {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
}

/* 열 묶음 뱃지 — headers[].group 을 준 열에만 붙는다.
   색은 headers[].groupClass 로 화면이 정한다(기본은 회색). */
.header-group-badge {
    display: inline-block;
    max-width: 100%;
    margin-bottom: 2px;
    padding: 0 6px;
    border-radius: 8px;
    background: #eef1f5;
    color: #7b8694;
    font-size: 10px;
    font-weight: 600;
    line-height: 16px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &.is-blue  { background: #e8f0fb; color: #2f6bbd; }
    &.is-green { background: #e8f6ee; color: #1d7a45; }
}

.help-icon-bottom {
    color: #6c757d;
    cursor: pointer;
    font-size: 12px;
    transition: color 0.2s ease;

    &:hover {
        color: #007bff;
    }

    &.active {
        color: #007bff;
    }
}

/* Tooltip Display Styles */
.tooltip-display {
    background: white;
    border: 1px solid #ddd;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    max-width: 350px;
    min-width: 250px;
    z-index: 1000;
}

.tooltip-content {
    padding: 0;
}

.tooltip-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background-color: #f8f9fa;
    border-bottom: 1px solid #dee2e6;
    border-radius: 8px 8px 0 0;
    font-size: 14px;
}

.tooltip-close {
    cursor: pointer;
    color: #6c757d;
    font-size: 14px;

    &:hover {
        color: #dc3545;
    }
}

.tooltip-body {
    padding: 16px;
    font-size: 13px;
    line-height: 1.5;
    color: #495057;
}

/* 헤더 우클릭 컨텍스트 메뉴 */
.col-context-menu {
    position: fixed;
    min-width: 180px;
    background: #fff;
    border: 1px solid #d0d4dc;
    border-radius: 6px;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
    z-index: 2070;
    padding: 4px 0;
    font-size: 13px;
    user-select: none;
}

.col-menu-item {
    position: relative;
    padding: 7px 14px;
    cursor: pointer;
    color: #333;
    display: flex;
    align-items: center;
    justify-content: space-between;
    white-space: nowrap;

    &:hover {
        background-color: #f0f3f9;
    }

    &.disabled {
        color: #aaa;
        cursor: not-allowed;

        &:hover {
            background-color: transparent;
        }
    }

    &.has-submenu {
        padding-right: 10px;
    }
}

.submenu-arrow {
    margin-left: 12px;
    font-size: 10px;
    color: #888;
}

.col-submenu {
    position: absolute;
    top: -5px;
    left: 100%;
    min-width: 200px;
    max-height: 320px;
    overflow-y: auto;
    background: #fff;
    border: 1px solid #d0d4dc;
    border-radius: 6px;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
    padding: 4px 0;
}

/* 컬럼 필터 */
.header-label-row {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.col-filter-icon {
    color: #6c757d;
    cursor: pointer;
    font-size: 11px;
    opacity: 0;
    transition: opacity 0.15s ease, color 0.15s ease;

    &.active {
        opacity: 1;
        color: #1867c0;
    }

    &:hover {
        color: #1867c0;
    }
}

th:hover .col-filter-icon {
    opacity: 0.55;
}

.filter-clear-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #eaf1fb;
    border: 1px solid #b6cdf0;
    border-radius: 4px;
    height: 40px;
    padding: 0 12px;
    margin-right: 12px;
    color: #1867c0;
    font-size: 13px;
    white-space: nowrap;

    &:hover {
        background: #dce8f9;
    }
}

.col-filter-panel {
    position: fixed;
    width: 264px;
    background: #fff;
    border: 1px solid #d0d4dc;
    border-radius: 6px;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
    z-index: 2080;
    padding: 8px 0;
    font-size: 13px;
    user-select: none;

    .cf-title {
        padding: 2px 12px 8px;
        font-weight: 600;
        color: #333;
        border-bottom: 1px solid #eceef3;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .cf-sort {
        display: flex;
        gap: 6px;
        padding: 8px 12px;
        border-bottom: 1px solid #eceef3;
    }

    .cf-sort-btn {
        flex: 1;
        border: 1px solid #dcdfe8;
        border-radius: 4px;
        padding: 5px 0;
        background: #fff;
        color: #444;
        font-size: 12px;

        &:hover {
            background: #f0f3f9;
        }
    }

    .cf-tabs {
        display: flex;
        border-bottom: 1px solid #eceef3;
    }

    .cf-tab {
        flex: 1;
        text-align: center;
        padding: 7px 0;
        cursor: pointer;
        color: #777;

        &.active {
            color: #1867c0;
            font-weight: 600;
            box-shadow: inset 0 -2px 0 #1867c0;
        }
    }

    .cf-values,
    .cf-condition {
        padding: 8px 12px;
    }

    .cf-search {
        width: 100%;
        border: 1px solid #dcdfe8;
        border-radius: 4px;
        padding: 4px 8px;
        margin-bottom: 6px;
        font-size: 12px;
        outline: none;

        &:focus {
            border-color: #1867c0;
        }
    }

    .cf-option-list {
        max-height: 190px;
        overflow-y: auto;
    }

    .cf-option {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 3px 2px;
        margin: 0;
        cursor: pointer;

        &:hover {
            background: #f0f3f9;
        }

        &.cf-option-all {
            border-bottom: 1px solid #eceef3;
            padding-bottom: 5px;
            margin-bottom: 3px;
            font-weight: 600;
        }
    }

    .cf-option-label {
        flex: 1;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .cf-option-count {
        color: #999;
        font-size: 11px;
    }

    .cf-empty,
    .cf-hint {
        color: #999;
        font-size: 11px;
        padding: 6px 2px 0;
    }

    .cf-cond-row {
        display: flex;
        gap: 6px;
        margin-bottom: 6px;
    }

    .cf-cond-op {
        width: 96px;
        border: 1px solid #dcdfe8;
        border-radius: 4px;
        padding: 4px 6px;
        font-size: 12px;
        background: #fff;
        outline: none;
    }

    .cf-cond-val {
        flex: 1;
        min-width: 0;
        border: 1px solid #dcdfe8;
        border-radius: 4px;
        padding: 4px 8px;
        font-size: 12px;
        outline: none;

        &:focus {
            border-color: #1867c0;
        }
    }

    .cf-actions {
        display: flex;
        gap: 6px;
        padding: 8px 12px 2px;
        border-top: 1px solid #eceef3;
    }

    .cf-btn {
        flex: 1;
        border: 1px solid #dcdfe8;
        border-radius: 4px;
        padding: 5px 0;
        background: #fff;
        color: #555;
        font-size: 12px;

        &:hover {
            background: #f0f3f9;
        }

        &.cf-btn-apply {
            background: #1867c0;
            border-color: #1867c0;
            color: #fff;

            &:hover {
                background: #145aa8;
            }
        }
    }
}

/* 즐겨찾기 추가 버튼 */
.fav-add-btn {
    background: transparent;
    border: 1px solid #DCDFE8;
    border-radius: 4px;
    width: 40px;
    height: 40px;
    margin-right: 12px;
    color: #adb5bd;
    cursor: pointer;
    font-size: 16px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: relative;
    z-index: 3;
    transition: all 0.15s;

    &:hover:not(:disabled) {
        color: #fbc02d;
        border-color: #fbc02d;
        background: #fffdf3;
    }

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }
}

.fav-add-input-wrap {
    margin-right: 12px;
    display: flex;
    align-items: center;
    position: relative;
    z-index: 3;
}

.fav-add-input {
    width: 160px;
    height: 40px;
    padding: 0 10px;
    border: 1px solid #fbc02d;
    border-radius: 4px;
    outline: none;
    font-size: 14px;
    background: #fffdf3;

    &:focus {
        border-color: #f57f17;
        box-shadow: 0 0 0 2px rgba(251, 192, 45, 0.2);
    }
}

/* 즐겨찾기 칩 행 */
.fav-row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: -8px;
    margin-bottom: 12px;
}

.fav-chip {
    display: inline-flex;
    align-items: center;
    background: #f4f5fa;
    border: 1px solid #DCDFE8;
    border-radius: 16px;
    padding: 4px 6px 4px 12px;
    font-size: 12px;
    line-height: 1.2;
    color: #333;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s, color 0.15s;
    max-width: 220px;
    height: 28px;

    &:hover {
        background: #e8eaf2;
        border-color: #b0b6c5;
    }

    &--active {
        background: #fff3cd;
        border-color: #fbc02d;
        color: #6b4f00;
        font-weight: 500;

        &:hover {
            background: #ffe8a5;
        }

        .fav-chip-remove {
            opacity: 1;
        }
    }

    .fav-chip-name {
        max-width: 160px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .fav-chip-remove {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        margin-left: 6px;
        width: 18px;
        height: 18px;
        border-radius: 50%;
        font-size: 14px;
        color: #888;
        opacity: 0;
        transition: opacity 0.15s, background 0.15s, color 0.15s;
        line-height: 1;

        &:hover {
            background: #dc3545;
            color: #fff;
        }
    }

    &:hover .fav-chip-remove {
        opacity: 1;
    }
}
</style>